import React, { useEffect, useState } from "react";
import { DetailsSection, Line } from "components/DetailsSection";
import { Hero } from "components/generic/Hero";
import { ServiceCentreDetailsCard } from "components/ServiceCentreDetailsCard";
import { Button } from "react-bootstrap";
import { LoadingBar } from "components/LoadingBar";
import { extractWorkDetailsFromExistingBooking } from "helpers/BookingConversionHelper";
import { scrollToTop } from "helpers/ScrollingHelper";
import { convertWorkRequiredToLines } from "helpers/WorkHelper";
import dayjs from "dayjs";
import config from "clientappsetting.json";
import { Icons } from "components/icons/Icons";
import { BookingSummaryInformation, ContactPreferenceType } from "generated/types/types";
import { assertNotNull, formattedContactPreference } from "helpers/Helpers";
import { useTranslation } from "react-i18next";
import { ApiUrlResolver } from "utils/ApiUrlResolver";
import { useSelector } from "react-redux";
import { RootState } from "redux/store/store";


interface Props {
    encryptedBookingId: string;
}

export const BookingConfirmation: React.FC<Props> = (props: Props) => {
    const { t } = useTranslation();
    useEffect(() => scrollToTop(), []);

    const [booking, setBooking] = useState<BookingSummaryInformation | null>(null);
    const countryCode = useSelector((state: RootState) => state.country.countryCode);

    useEffect(() => {
        fetch(ApiUrlResolver.getWebApiUrl(countryCode) + "/Booking/" + props.encryptedBookingId)
            .then(response => response.json())
            .then(data => {
                setBooking(data);
            });
    }, [props.encryptedBookingId]);

    if (booking === null) {
        return <LoadingBar fullscreen={true} />;
    }

    const serviceCentreDetails = booking.serviceCentreDetails;
    const getHeroDescription = () => {
        const driverDetails = booking.driverDetails;

        const ref = props.encryptedBookingId.split("-")[0];
        const centre = booking.serviceCentreDetails.name;
        let text = t("booking_confirmation_booking_reference", {ref, centre});

        const contactByEmail = driverDetails?.contactPreferences?.find(x => x === ContactPreferenceType.Email);
        const contactBySms = driverDetails?.contactPreferences?.find(x => x === ContactPreferenceType.Sms);
        if (contactByEmail && contactBySms) {
            text += t("booking_confirmation_email_and_sms");
        } else if (contactByEmail) {
            text += t("booking_confirmation_email");
        } else if (contactBySms) {
            text += t("booking_confirmation_sms");
        }
        return text;
    };

    const workDetails = extractWorkDetailsFromExistingBooking(booking, t);
    const workDescriptionLines = convertWorkRequiredToLines(workDetails.workRequired ?? [], workDetails.repairOtherText ?? "", t);

    const getBookingDatesLines = (): Line[] => {
        const lines: Line[] = [];
        lines.push({ text: `${dayjs(assertNotNull(booking.bookingDetails.firstPreferenceDate)).format("ddd Do MMM YYYY [at] HH:mm")}` });
        if (booking.bookingDetails.secondPreferenceDate) {
            lines.push({ text: `${dayjs(booking.bookingDetails.secondPreferenceDate).format("ddd Do MMM YYYY [at] HH:mm")}` });
        }
        if (booking.bookingDetails.thirdPreferenceDate) {
            lines.push({ text: `${dayjs(booking.bookingDetails.thirdPreferenceDate).format("ddd Do MMM YYYY [at] HH:mm")}` });
        }
        return lines;
    };

    const phone = booking?.serviceCentreDetails?.phoneNumber ?? "";
    const regNo = booking?.vehicleDetails.registrationNumber ?? "";
    const odo = booking?.vehicleDetails.odoReading ?? "";

    return (
        <div className="body-container--booking-confirmation">
            <div className="row">
                <div className="col-12">
                    <Hero heading={t("booking_confirmation_booking_requested")} description={getHeroDescription()} className="small-when-printing" />
                </div>
                <div className="col-xl-5 booking-details">
                    <DetailsSection
                        heading={t("booking_confirmation_garage")}
                        iconComponent={Icons.GarageIcon}
                        lines={[
                            { text: `${booking.serviceCentreDetails.name} - ${booking.serviceCentreDetails.addressLines[1]}` },
                            { text: t("booking_confirmation_telephone", {phone}) },
                            { text: t("booking_confirmation_requested_dates") },
                            ...getBookingDatesLines(),
                            ...workDescriptionLines,
                        ]}
                    />
                    <DetailsSection
                        heading={t("booking_confirmation_vehicle_details")}
                        iconComponent={Icons.VehicleIcon}
                        lines={[
                            { text: `${booking.vehicleDetails.make} ${booking.vehicleDetails.model} ${booking.vehicleDetails.derivative}` },
                            { text: t("booking_confirmation_reg_no", {regNo}) },
                            { text: t("booking_confirmation_odo_reading", {odo}) },
                        ]}
                    />
                    <DetailsSection
                        heading={t("booking_confirmation_driver_details")}
                        iconComponent={Icons.DriverIcon}
                        lines={[
                            { text: `${booking.driverDetails.driverName}` },
                            { text: `${booking.driverDetails.phoneNumber}` },
                            { text: `${booking.driverDetails.email}` },
                            { prefix: t("booking_confirmation_contact"), text: formattedContactPreference(booking.driverDetails.contactPreferences) },
                        ]}
                    />
                    <div className="row no-print">
                        <div className="col-md-12">
                            <Button variant="primary" onClick={() => window.print()}>
                                {t("booking_confirmation_print")}
                            </Button>
                        </div>
                        <div className="col-md-12">
                            <Button variant="primary" onClick={() => window.location.reload()}>
                                {t("booking_confirmation_new_booking")}
                            </Button>
                        </div>
                    </div>
                </div>
                <div className="col-xl-7">
                    <ServiceCentreDetailsCard serviceCentreDetails={serviceCentreDetails} />
                </div>
            </div>
        </div>
    );
};
